/**
 * Strip internal object UUIDs and IDs from user-visible text (#4615).
 * Shared with AssistantMarkdown.tsx and used to sanitize the persisted
 * `thinking` field at the message-mapping chokepoint.
 *
 * Strategy: protect xavior://object/<type>/<uuid> link URLs with opaque
 * placeholders before stripping bare UUIDs, then restore them afterwards.
 * This avoids over-broad lookbehinds that would also preserve UUIDs in
 * unrelated slash-prefixed contexts (file paths, REST examples, etc.).
 * Use a placeholder sentinel that cannot appear in any LLM-generated markdown.
 * We use a long pseudo-private-use string rather than control characters to
 * satisfy the no-control-regex lint rule while still being unique enough
 * that it can never collide with real content.
 */
export function stripInternalUUIDs(text: string): string {
  let output = text;

  // #5200: neutralize deep-links emitted with an EMPTY object id
  // (e.g. `[England Trip Planning](xavior://object/doc/)`) — unnavigable dead
  // links. Unwrap the markdown form to its label; drop the bare form. Runs
  // BEFORE the valid-link protection below — the empty-id pattern requires `/)`
  // with nothing between, so it can never match a full-uuid link. Mirrors the
  // backend stripBareUuids (apps/x-agent/src/lib/sanitize.ts) — keep in sync.
  // The FE runs on the reassembled full text, so it also catches a link split
  // across backend stream-chunk boundaries. Cheap substring guard first (the
  // common case has no object link).
  if (output.includes("xavior://object/")) {
    output = output.replace(/\[([^\]]*)\]\(xavior:\/\/object\/[a-z]+\/\)/gi, "$1");
    output = output.replace(/\s*\(xavior:\/\/object\/[a-z]+\/\)/gi, "");
  }

  const XOBJ_SENTINEL = '\u{E0078}\u{E006F}\u{E0062}\u{E006A}';
  const xaviorLinkPlaceholders: string[] = [];
  output = output.replace(
    /\(xavior:\/\/object\/[a-z]+\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)/gi,
    (match) => {
      const idx = xaviorLinkPlaceholders.length;
      xaviorLinkPlaceholders.push(match);
      return `${XOBJ_SENTINEL}${idx}${XOBJ_SENTINEL}`;
    },
  );

  // UUIDs in parentheses: (ID: xxxxxxxx-...), (id=xxxxxxxx-...), or (xxxxxxxx-...).
  // The label group consumes the id:/id=/ID: wrapper as a unit so the whole token
  // is removed — otherwise the inline pass below orphans an empty `(id=)` shell
  // into chat (the agent echoes a `- updated (id=<uuid>)` bullet; #calendar-104).
  output = output.replace(
    /\s*\((?:id\s*[:=]\s*)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)/gi,
    "",
  );
  // Bare UUIDs on their own line
  output = output.replace(/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/gim, "");
  // Bare UUIDs inline — match at any word boundary so trailing punctuation
  // (e.g. "Updated contact 08d6b7d3-...-ce8db91706b7.") is also caught.
  // Also collapse the preceding space so the resulting text isn't littered
  // with double spaces or a lone space before a period.
  output = output.replace(/\s*\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "");
  // Sweep any orphaned empty id-shell left by an echoed bullet whose UUID was
  // stripped before this pass (e.g. `(id=)`, `( id : )`).
  output = output.replace(/\s*\(\s*id\s*[:=]?\s*\)/gi, "");

  // Restore xavior://object link URLs now that bare UUIDs have been stripped.
  // Sentinel chars are in the Unicode Tag block (U+E0000..U+E007F) — used for
  // language tags, never generated by LLMs, safe to round-trip through JS strings.
  output = output.replace(
    new RegExp(`${XOBJ_SENTINEL}(\\d+)${XOBJ_SENTINEL}`, 'g'),
    (_, idx) => xaviorLinkPlaceholders[Number(idx)],
  );
  output = output.replace(/\s*\((?:ID:\s*)?[A-Za-z0-9_-]{16,}\)/g, "");
  output = output.replace(/\b(moduleId|module_id|conversationId|conversation_id)\s*[:=]\s*[A-Za-z0-9_-]{6,}\b/gi, "$1: [hidden]");
  output = output.replace(/^\s*ID:\s*[A-Za-z0-9_-]{6,}\s*$/gim, "");

  // Labeled short/truncated hex ids + internal field=value pairs (#4761).
  // Mirrors the backend sanitizeOutput passes (apps/x-agent/src/lib/sanitize.ts;
  // cross-package sharing is barred by import rules, so the two stay parallel).
  // NARROW by design: the short-id pass needs an id LABEL before the hex run, so
  // bare short tokens like `abc-123-def` (the keep-short-identifiers test) are
  // untouched; the field=value pass is anchored to a small enumerated key set so
  // it can't eat ordinary prose or URL query strings.
  output = output.replace(/\b(id|object\s?id|objectId)([:\s]+)[0-9a-f]{6,}\b/gi, "$1$2[hidden]");
  // The key MUST follow whitespace or start-of-string so the pass stays off URL
  // query strings ("?type=task&status=open"), where the key follows `?`/`&`.
  output = output.replace(
    /(?:^|\s)(?:status|stage|objectType|objectId|kind|moduleId|archived_at|receiptType)\s*=\s*\S+/gi,
    "",
  );

  return output;
}
